import { createSlice } from '@reduxjs/toolkit'
import { ControlPlaneState } from '../../types/controlPlane'
import { CONTROL_PLANE_TOKEN_KEY, CONTROL_PLANE_OWNER_KEY } from '../../config/controlPlaneApi'
import {
  signupEmail,
  loginEmail,
  loginGoogle,
  checkSlug,
  createOrganization,
  fetchMyOrganizations,
  fetchOrganizationLicense,
  changeOrganizationLicense,
} from './controlPlaneActions'

const storedOwner = localStorage.getItem(CONTROL_PLANE_OWNER_KEY)

const initialState: ControlPlaneState = {
  token: localStorage.getItem(CONTROL_PLANE_TOKEN_KEY) || null,
  owner: storedOwner ? JSON.parse(storedOwner) : null,
  authLoading: false,
  authError: null,
  organizations: [],
  organizationsLoading: false,
  creating: false,
  createError: null,
  lastCreateResult: null,
  billing: {},
  billingLoading: false,
  billingError: null,
}

export const controlPlaneSlice = createSlice({
  name: 'controlPlane',
  initialState,
  reducers: {
    controlPlaneLogout: (state) => {
      state.token = null
      state.owner = null
      state.organizations = []
      state.lastCreateResult = null
      localStorage.removeItem(CONTROL_PLANE_TOKEN_KEY)
      localStorage.removeItem(CONTROL_PLANE_OWNER_KEY)
    },
    clearCreateResult: (state) => {
      state.lastCreateResult = null
      state.createError = null
    },
  },
  extraReducers: (builder) => {
    const onSession = (state: ControlPlaneState, action: any) => {
      state.authLoading = false
      state.token = action.payload.token
      state.owner = action.payload.owner
      localStorage.setItem(CONTROL_PLANE_TOKEN_KEY, action.payload.token)
      localStorage.setItem(CONTROL_PLANE_OWNER_KEY, JSON.stringify(action.payload.owner))
    }

    builder
      .addCase(signupEmail.pending, (state) => {
        state.authLoading = true
        state.authError = null
      })
      .addCase(signupEmail.fulfilled, onSession)
      .addCase(signupEmail.rejected, (state, action) => {
        state.authLoading = false
        state.authError = action.payload ?? 'Signup failed.'
      })
      .addCase(loginEmail.pending, (state) => {
        state.authLoading = true
        state.authError = null
      })
      .addCase(loginEmail.fulfilled, onSession)
      .addCase(loginEmail.rejected, (state, action) => {
        state.authLoading = false
        state.authError = action.payload ?? 'Login failed.'
      })
      .addCase(loginGoogle.pending, (state) => {
        state.authLoading = true
        state.authError = null
      })
      .addCase(loginGoogle.fulfilled, onSession)
      .addCase(loginGoogle.rejected, (state, action) => {
        state.authLoading = false
        state.authError = action.payload ?? 'Google sign-in failed.'
      })
      .addCase(checkSlug.rejected, (state, action) => {
        state.createError = action.payload ?? null
      })
      .addCase(createOrganization.pending, (state) => {
        state.creating = true
        state.createError = null
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.creating = false
        state.lastCreateResult = action.payload
        state.organizations = [action.payload.organization, ...state.organizations]
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.creating = false
        state.createError = action.payload ?? 'Could not create the organization.'
      })
      .addCase(fetchMyOrganizations.pending, (state) => {
        state.organizationsLoading = true
      })
      .addCase(fetchMyOrganizations.fulfilled, (state, action) => {
        state.organizationsLoading = false
        state.organizations = action.payload
      })
      .addCase(fetchMyOrganizations.rejected, (state) => {
        state.organizationsLoading = false
      })
      .addCase(fetchOrganizationLicense.pending, (state) => {
        state.billingLoading = true
        state.billingError = null
      })
      .addCase(fetchOrganizationLicense.fulfilled, (state, action) => {
        state.billingLoading = false
        state.billing[action.payload.organizationId] = action.payload.license
      })
      .addCase(fetchOrganizationLicense.rejected, (state, action) => {
        state.billingLoading = false
        state.billingError = action.payload ?? 'Could not load the license.'
      })
      .addCase(changeOrganizationLicense.pending, (state) => {
        state.billingLoading = true
        state.billingError = null
      })
      .addCase(changeOrganizationLicense.fulfilled, (state, action) => {
        state.billingLoading = false
        state.billing[action.payload.organizationId] = action.payload.license
      })
      .addCase(changeOrganizationLicense.rejected, (state, action) => {
        state.billingLoading = false
        state.billingError = action.payload ?? 'Could not change the plan.'
      })
  },
})

export const { controlPlaneLogout, clearCreateResult } = controlPlaneSlice.actions
export default controlPlaneSlice.reducer
